'use client';

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Server, Activity, ShieldAlert, Zap, Database, Bot, RefreshCw } from 'lucide-react';

interface NodeStat {
    id: string; name: string; hw: string; role: string;
    online: boolean; latency: number; service: string;
    memoryMB: number | null; heapUsedMB: number | null;
    uptimeHours: number | null;
}

interface AgentData {
    summary: {
        totalNodes: number; onlineNodes: number; offlineNodes: number;
        uptimeRate: number; avgLatency: number; totalMemoryMB: number;
    };
    nodeStats: NodeStat[];
    latencyBuckets: { fast: number; normal: number; slow: number; timeout: number };
    checkedAt: string;
}

export default function AgentStats({ isDark }: { isDark: boolean }) {
    const [data, setData] = useState<AgentData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchStats = () => {
        fetch('/api/agents/stats')
            .then(r => r.json())
            .then(d => { setData(d); setLoading(false); })
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        fetchStats();
        const iv = setInterval(fetchStats, 30000); // 30초마다 새로고침
        return () => clearInterval(iv);
    }, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!data?.summary) return <div className={`text-center py-8 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>에이전트 데이터 없음</div>;

    const { summary, nodeStats, latencyBuckets } = data;

    return (
        <div className="flex flex-col gap-3">
            {/* ── Row 1: 요약 KPI ── */}
            <div className="grid grid-cols-5 gap-2 max-lg:grid-cols-3 max-sm:grid-cols-2">
                {[
                    { label: '전체 노드', value: summary.totalNodes, unit: '개', icon: <Server className="w-5 h-5 text-blue-500" />, grad: 'from-blue-500 to-blue-600' },
                    { label: '온라인', value: summary.onlineNodes, unit: `(${summary.uptimeRate}%)`, icon: <Activity className={`w-5 h-5 ${summary.uptimeRate === 100 ? 'text-emerald-500' : 'text-yellow-500'}`} />, grad: summary.uptimeRate === 100 ? 'from-emerald-500 to-emerald-600' : 'from-yellow-500 to-yellow-600' },
                    { label: '오프라인', value: summary.offlineNodes, unit: '개', icon: <ShieldAlert className={`w-5 h-5 ${summary.offlineNodes > 0 ? 'text-red-500' : 'text-emerald-500'}`} />, grad: summary.offlineNodes > 0 ? 'from-red-500 to-red-600' : 'from-gray-500 to-gray-600' },
                    { label: '평균 응답', value: summary.avgLatency, unit: 'ms', icon: <Zap className={`w-5 h-5 ${summary.avgLatency < 500 ? 'text-green-500' : 'text-orange-500'}`} />, grad: summary.avgLatency < 500 ? 'from-green-500 to-green-600' : 'from-orange-500 to-orange-600' },
                    { label: '총 메모리', value: summary.totalMemoryMB, unit: 'MB', icon: <Database className="w-5 h-5 text-purple-500" />, grad: 'from-purple-500 to-purple-600' },
                ].map(k => (
                    <motion.div key={k.label} whileHover={{ y: -2 }}
                        className={`rounded-xl p-3 border ${isDark ? 'bg-white/[0.04] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-lg">{k.icon}</span>
                            <div className={`text-xl font-bold bg-gradient-to-r ${k.grad} bg-clip-text text-transparent`}>{k.value}</div>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                            <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{k.label}</span>
                            <span className={`text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>{k.unit}</span>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* ── Row 2: 노드 그리드 + 레이턴시 분포 ── */}
            <div className="grid grid-cols-3 gap-3 max-lg:grid-cols-1">
                {/* 노드 상세 목록 */}
                <div className={`col-span-2 rounded-xl border p-4 ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className={`text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}><Bot className="w-4 h-4 text-indigo-400" /> 노드 상태 (실시간)</h3>
                        <button onClick={fetchStats} className={`text-[10px] px-2 py-1 rounded-md cursor-pointer flex items-center gap-1 ${isDark ? 'text-indigo-400 hover:bg-white/5' : 'text-indigo-600 hover:bg-indigo-50'}`}>
                            <RefreshCw className="w-3 h-3" /> 새로고침
                        </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 max-sm:grid-cols-2">
                        {nodeStats.map(n => (
                            <motion.div key={n.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                                className={`rounded-lg p-3 border transition-all ${n.online
                                    ? isDark ? 'bg-green-500/[0.03] border-green-500/20' : 'bg-green-50 border-green-200'
                                    : isDark ? 'bg-red-500/[0.03] border-red-500/20 opacity-60' : 'bg-red-50 border-red-200 opacity-60'
                                    }`}>
                                <div className="flex items-center gap-2">
                                    <div className={`w-2 h-2 rounded-full ${n.online ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                                    <span className={`text-xs font-bold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>{n.name}</span>
                                </div>
                                <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{n.role}</div>
                                <div className="flex gap-2 mt-1.5 text-[9px]">
                                    <span className={n.online ? 'text-green-400' : 'text-red-400'}>{n.online ? `${n.latency}ms` : 'offline'}</span>
                                    {n.memoryMB !== null && <span className={isDark ? 'text-purple-400' : 'text-purple-600'}>{n.memoryMB}MB</span>}
                                    {n.uptimeHours !== null && <span className={isDark ? 'text-blue-400' : 'text-blue-600'}>{n.uptimeHours}h</span>}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* 레이턴시 분포 */}
                <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.03] border-white/[0.06]' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <h3 className={`text-xs font-semibold uppercase tracking-wider mb-3 flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}><Zap className="w-4 h-4 text-amber-500" /> 응답 분포</h3>
                    <div className="space-y-3">
                        {[
                            { label: '빠름 (<200ms)', value: latencyBuckets.fast, color: 'bg-green-500', total: summary.totalNodes },
                            { label: '보통 (200~1s)', value: latencyBuckets.normal, color: 'bg-yellow-500', total: summary.totalNodes },
                            { label: '느림 (>1s)', value: latencyBuckets.slow, color: 'bg-orange-500', total: summary.totalNodes },
                            { label: '타임아웃', value: latencyBuckets.timeout, color: 'bg-red-500', total: summary.totalNodes },
                        ].map(b => (
                            <div key={b.label}>
                                <div className="flex items-center justify-between mb-1">
                                    <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{b.label}</span>
                                    <span className={`text-[10px] font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{b.value}</span>
                                </div>
                                <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-white/[0.06]' : 'bg-gray-100'}`}>
                                    <motion.div initial={{ width: 0 }} animate={{ width: `${b.total > 0 ? (b.value / b.total) * 100 : 0}%` }}
                                        transition={{ duration: 0.8 }}
                                        className={`h-full rounded-full ${b.color}`} />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* 가동률 게이지 */}
                    <div className="mt-6 text-center">
                        <div className="relative inline-block">
                            <svg width="120" height="70" viewBox="0 0 120 70">
                                <path d="M 10 65 A 50 50 0 0 1 110 65" fill="none" stroke={isDark ? '#333' : '#e5e7eb'} strokeWidth="8" strokeLinecap="round" />
                                <motion.path d="M 10 65 A 50 50 0 0 1 110 65" fill="none" stroke={summary.uptimeRate === 100 ? '#22c55e' : '#eab308'} strokeWidth="8" strokeLinecap="round"
                                    initial={{ pathLength: 0 }} animate={{ pathLength: summary.uptimeRate / 100 }}
                                    transition={{ duration: 1.5, ease: 'easeOut' }}
                                    style={{ strokeDasharray: 1, strokeDashoffset: 0 }} />
                            </svg>
                            <div className="absolute inset-0 flex items-end justify-center pb-1">
                                <span className={`text-lg font-bold ${summary.uptimeRate === 100 ? 'text-green-400' : 'text-yellow-400'}`}>{summary.uptimeRate}%</span>
                            </div>
                        </div>
                        <div className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>가동률</div>
                    </div>
                </div>
            </div>

            <div className={`text-right text-[10px] ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                마지막 체크: {new Date(data.checkedAt).toLocaleString('ko-KR', { timeZone: 'Asia/Seoul' })} · 30초마다 자동 갱신
            </div>
        </div>
    );
}
